import { RichTextEditor as MantineRichTextEditor } from "@mantine/tiptap";
import clsx from "clsx";
import { componentWrapper } from "../../utilities";
import { Blockquote } from "./blockquote";
import { Bold } from "./bold";
import { BulletList } from "./bullet-list";
import { Code } from "./code";
import { Content } from "./content";
import { Control } from "./control";
import { ControlsGroup } from "./controls-group";
import { Italic } from "./italic";
import { Link } from "./link";
import { OrderedList } from "./ordered-list";
import { Strikethrough } from "./strikethrough";
import { Toolbar } from "./toolbar";
import { Underline } from "./underline";
import { Unlink } from "./unlink";
import styles from "./rich-text-editor.module.scss";
import type { CustomRichTextEditorFactory, IProps } from "./rich-text-editor.types";

/**
 * EdgeTier wrapper around Mantine's RichTextEditor. Provides a consistent default look (subtle control variant, our
 * border / radius / focus colours) and forwards every Mantine prop through. Sub-components live in their own files and
 * are re-exposed on the root so consumers never need to import from `@mantine/tiptap` directly:
 *
 * ```tsx
 * <RichTextEditor editor={editor}>
 *   <RichTextEditor.Toolbar>
 *     <RichTextEditor.ControlsGroup>
 *       <RichTextEditor.Bold />
 *       <RichTextEditor.Italic />
 *     </RichTextEditor.ControlsGroup>
 *   </RichTextEditor.Toolbar>
 *   <RichTextEditor.Content />
 * </RichTextEditor>
 * ```
 *
 * Consumers must import the Mantine tiptap stylesheet (`@mantine/tiptap/styles.css`) at the application entry once;
 * Mantine ships toolbar icon/layout CSS through that bundle and it cannot be re-exported from this package.
 *
 * Each sub-component applies its own className directly (rather than relying on the parent's compound `classNames`
 * prop) — that's the only reliable way to get our overrides onto the rendered elements through Mantine's styles API.
 * The built-in controls (Bold, Italic, Link, …) are the exception: Mantine's control base drops `className`, so they pass
 * the control class through their own `classNames.control` instead.
 *
 * @param {IProps} props Props for the component.
 * @param {IProps["variant"]} props.variant Visual variant — defaults to "subtle".
 * @param {IProps["className"]} props.className Additional class names appended to the EdgeTier root class.
 * @returns {JSX.Element}
 */
const RichTextEditor = componentWrapper<CustomRichTextEditorFactory, IProps>(
    "RichTextEditor",
    ({ variant = "subtle", className, ...other }, ref) => {
        return (
            <MantineRichTextEditor ref={ref} variant={variant} className={clsx(styles["root"], className)} {...other} />
        );
    }
);

RichTextEditor.Toolbar = Toolbar as typeof MantineRichTextEditor.Toolbar;
RichTextEditor.Content = Content as typeof MantineRichTextEditor.Content;
RichTextEditor.ControlsGroup = ControlsGroup as typeof MantineRichTextEditor.ControlsGroup;
RichTextEditor.Control = Control as typeof MantineRichTextEditor.Control;
RichTextEditor.Bold = Bold as typeof MantineRichTextEditor.Bold;
RichTextEditor.Italic = Italic as typeof MantineRichTextEditor.Italic;
RichTextEditor.Underline = Underline as typeof MantineRichTextEditor.Underline;
RichTextEditor.Strikethrough = Strikethrough as typeof MantineRichTextEditor.Strikethrough;
RichTextEditor.Code = Code as typeof MantineRichTextEditor.Code;
RichTextEditor.BulletList = BulletList as typeof MantineRichTextEditor.BulletList;
RichTextEditor.Blockquote = Blockquote as typeof MantineRichTextEditor.Blockquote;
RichTextEditor.OrderedList = OrderedList as typeof MantineRichTextEditor.OrderedList;
RichTextEditor.Link = Link as typeof MantineRichTextEditor.Link;
RichTextEditor.Unlink = Unlink as typeof MantineRichTextEditor.Unlink;

export { RichTextEditor };
